import React from "react";
import { connect } from "react-redux";
import moment from "moment";
import { Button } from "../../Button";
import { Input, Select } from "../../Input";
import { Modal, DeleteModal } from "../../Modal";
import toastMessage from "../../../utils/toastMessage";
import centerQuery from "../../../utils/queries/bloodCenterQuery";
import fetchFieldActivityPlan, {
  createFieldActivityPlan,
  updateFieldActivityPlan,
  deleteFieldActivityPlan,
  recordFieldActivityPerformance,
} from "../../../utils/queries/fieldActivityPlanQuery";

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => {
  const y = currentYear - 2 + i;
  return { label: String(y), value: y };
});

const QUARTERS = [
  { label: "Q1 (Jan – Mar)", value: 1 },
  { label: "Q2 (Apr – Jun)", value: 2 },
  { label: "Q3 (Jul – Sep)", value: 3 },
  { label: "Q4 (Oct – Dec)", value: 4 },
];

const STATUS_OPTIONS = [
  { label: "Planned", value: "planned" },
  { label: "In Progress", value: "in_progress" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" },
];

const STATUS_COLORS = {
  planned: { bg: "#fff3cd", color: "#856404" },
  in_progress: { bg: "#cce5ff", color: "#004085" },
  completed: { bg: "#d4edda", color: "#155724" },
  cancelled: { bg: "#f8d7da", color: "#721c24" },
};

class FieldActivityPlan extends React.Component {
  state = {
    filterYear: currentYear,
    filterQuarter: null,
    filterStatus: null,
    centers: [],
    plans: [],
    isLoading: false,

    showModal: false,
    showDeleteModal: false,
    showPerformModal: false,
    showDetailModal: false,
    editRecord: null,
    deleteId: null,
    performRecord: null,
    detailRecord: null,

    formYear: currentYear,
    formQuarter: null,
    formActivityNumber: "",
    formActivityName: "",
    formDescription: "",
    formVenue: "",
    formParticipants: "",
    formResponsibleCenter: null,
    formPlannedStartDate: "",
    formPlannedEndDate: "",
    formPlannedBudget: "",
    formError: {},
    isSaving: false,
    isDeleting: false,

    perfStartDate: "",
    perfEndDate: "",
    perfVenue: "",
    perfParticipants: "",
    perfRemarks: "",
    perfStatus: null,
    isSavingPerf: false,

    viewMode: "table",
  };

  componentDidMount() {
    this.getCenters();
    this.fetchPlans();
  }

  getCenters = async () => {
    const data = await centerQuery(this.props.defaultLanguage, { type: "dropdown" });
    this.setState({ centers: data || [] });
  };

  fetchPlans = async () => {
    const { filterYear, filterQuarter, filterStatus } = this.state;
    this.setState({ isLoading: true });
    try {
      const params = { year: filterYear, limit: 200, page: 1 };
      if (filterQuarter?.value) params.quarter = filterQuarter.value;
      if (filterStatus?.value) params.status = filterStatus.value;

      const res = await fetchFieldActivityPlan(this.props.defaultLanguage, params);
      this.setState({ plans: res?.data || [], isLoading: false });
    } catch {
      this.setState({ isLoading: false });
    }
  };

  openAddModal = () => {
    this.setState({
      showModal: true,
      editRecord: null,
      formYear: this.state.filterYear,
      formQuarter: null,
      formActivityNumber: "",
      formActivityName: "",
      formDescription: "",
      formVenue: "",
      formParticipants: "",
      formResponsibleCenter: null,
      formPlannedStartDate: "",
      formPlannedEndDate: "",
      formPlannedBudget: "",
      formError: {},
    });
  };

  openEditModal = (record) => {
    const formQuarter = QUARTERS.find((q) => q.value === record.quarter) || null;
    const formResponsibleCenter = record.responsibleCenter
      ? { label: record.responsibleCenter.name || record.responsibleCenter._id, value: record.responsibleCenter._id || record.responsibleCenter }
      : null;

    this.setState({
      showModal: true,
      editRecord: record,
      formYear: record.year || currentYear,
      formQuarter,
      formActivityNumber: String(record.activityNumber || ""),
      formActivityName: record.activityName || "",
      formDescription: record.description || "",
      formVenue: record.venue || "",
      formParticipants: (record.participants || []).join(", "),
      formResponsibleCenter,
      formPlannedStartDate: record.plannedStartDate ? moment(record.plannedStartDate).format("YYYY-MM-DD") : "",
      formPlannedEndDate: record.plannedEndDate ? moment(record.plannedEndDate).format("YYYY-MM-DD") : "",
      formPlannedBudget: String(record.plannedBudget || ""),
      formError: {},
    });
  };

  openPerformModal = (record) => {
    this.setState({
      showPerformModal: true,
      performRecord: record,
      perfStartDate: record.performedStartDate ? moment(record.performedStartDate).format("YYYY-MM-DD") : "",
      perfEndDate: record.performedEndDate ? moment(record.performedEndDate).format("YYYY-MM-DD") : "",
      perfVenue: record.actualVenue || "",
      perfParticipants: (record.actualParticipants || []).join(", "),
      perfRemarks: record.remarks || "",
      perfStatus: STATUS_OPTIONS.find((s) => s.value === record.status) || null,
    });
  };

  openDetailModal = (record) => this.setState({ showDetailModal: true, detailRecord: record });
  closeDetailModal = () => this.setState({ showDetailModal: false, detailRecord: null });
  closeModal = () => this.setState({ showModal: false, editRecord: null });
  closePerformModal = () => this.setState({ showPerformModal: false, performRecord: null });

  validateForm = () => {
    const { formQuarter, formActivityNumber, formActivityName, formPlannedStartDate, formPlannedEndDate } = this.state;
    const errors = {};
    if (!formQuarter) errors.formQuarter = "Required";
    if (!formActivityNumber) errors.formActivityNumber = "Required";
    if (!formActivityName.trim()) errors.formActivityName = "Required";
    if (!formPlannedStartDate) errors.formPlannedStartDate = "Required";
    if (!formPlannedEndDate) errors.formPlannedEndDate = "Required";
    this.setState({ formError: errors });
    return Object.keys(errors).length === 0;
  };

  saveRecord = async () => {
    if (!this.validateForm()) return;
    const {
      formYear, formQuarter, formActivityNumber, formActivityName, formDescription,
      formVenue, formParticipants, formResponsibleCenter, formPlannedStartDate,
      formPlannedEndDate, formPlannedBudget, editRecord,
    } = this.state;

    this.setState({ isSaving: true });
    try {
      const body = {
        year: Number(formYear),
        quarter: formQuarter.value,
        activityNumber: Number(formActivityNumber),
        activityName: formActivityName.trim(),
        description: formDescription.trim(),
        venue: formVenue.trim(),
        participants: formParticipants.split(",").map((s) => s.trim()).filter(Boolean),
        responsibleCenter: formResponsibleCenter?.value || null,
        plannedStartDate: formPlannedStartDate,
        plannedEndDate: formPlannedEndDate,
        plannedBudget: Number(formPlannedBudget) || 0,
      };

      if (editRecord) {
        body.id = editRecord._id;
        await updateFieldActivityPlan(this.props.defaultLanguage, body);
        toastMessage("success", "Activity updated");
      } else {
        await createFieldActivityPlan(this.props.defaultLanguage, body);
        toastMessage("success", "Activity created");
      }

      this.closeModal();
      this.fetchPlans();
    } catch (err) {
      toastMessage("error", err?.response?.data?.error || "Save failed");
    }
    this.setState({ isSaving: false });
  };

  savePerformance = async () => {
    const { performRecord, perfStartDate, perfEndDate, perfVenue, perfParticipants, perfRemarks, perfStatus } = this.state;
    this.setState({ isSavingPerf: true });
    try {
      await recordFieldActivityPerformance(this.props.defaultLanguage, {
        id: performRecord._id,
        performedStartDate: perfStartDate || null,
        performedEndDate: perfEndDate || null,
        actualVenue: perfVenue.trim(),
        actualParticipants: perfParticipants.split(",").map((s) => s.trim()).filter(Boolean),
        remarks: perfRemarks,
        status: perfStatus?.value || "completed",
      });
      toastMessage("success", "Performance recorded");
      this.closePerformModal();
      this.fetchPlans();
    } catch (err) {
      toastMessage("error", err?.response?.data?.error || "Save failed");
    }
    this.setState({ isSavingPerf: false });
  };

  confirmDelete = (id) => this.setState({ showDeleteModal: true, deleteId: id });
  closeDeleteModal = () => this.setState({ showDeleteModal: false, deleteId: null });

  deleteRecord = async () => {
    this.setState({ isDeleting: true });
    try {
      await deleteFieldActivityPlan(this.props.defaultLanguage, this.state.deleteId);
      toastMessage("success", "Activity deleted");
      this.closeDeleteModal();
      this.fetchPlans();
    } catch {
      toastMessage("error", "Delete failed");
    }
    this.setState({ isDeleting: false });
  };

  renderGanttView() {
    const { plans, filterYear } = this.state;
    if (!plans.length) return null;

    const yearStart = new Date(filterYear, 0, 1);
    const yearEnd = new Date(filterYear, 11, 31);
    const totalDays = (yearEnd - yearStart) / 86400000 + 1;

    const getBarStyle = (startDate, endDate, color, opacity = 1) => {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const left = ((start - yearStart) / 86400000 / totalDays) * 100;
      const width = Math.max(((end - start) / 86400000 + 1) / totalDays * 100, 0.5);
      return { position: "absolute", left: `${left}%`, width: `${width}%`, height: 12, borderRadius: 3, background: color, opacity, top: 2 };
    };

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    return (
      <div id="printable-field-activity-gantt">
        <style>{`@media print { body > *:not(#printable-field-activity-gantt) { display: none; } #printable-field-activity-gantt { display: block !important; } .no-print { display: none !important; } }`}</style>
        <div style={{ textAlign: "center", fontWeight: "bold", fontSize: 15, marginBottom: 4 }}>FIELD ACTIVITY PLAN — GANTT VIEW — {filterYear}</div>
        <div style={{ textAlign: "center", fontSize: 12, color: "#555", marginBottom: 12 }}>Rwanda Biomedical Centre / Blood Transfusion Division</div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: "#1a5276", color: "#fff" }}>
                <th style={{ ...thStyle, width: 30 }}>#</th>
                <th style={{ ...thStyle, width: 200 }}>Activity</th>
                <th style={{ ...thStyle, width: 60 }}>Q</th>
                <th style={{ ...thStyle, width: 80 }}>Status</th>
                <th style={thStyle}>
                  <div style={{ position: "relative", height: 16 }}>
                    {months.map((m, i) => (
                      <span key={m} style={{ position: "absolute", left: `${(i / 12) * 100}%`, fontSize: 9 }}>{m}</span>
                    ))}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p._id} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ ...tdStyle, textAlign: "center" }}>{p.activityNumber}</td>
                  <td style={{ ...tdStyle, cursor: "pointer", color: "#2980b9", textDecoration: "underline" }}
                    onClick={() => this.openDetailModal(p)}>
                    {p.activityName}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>Q{p.quarter}</td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <span style={statusBadge(p.status)}>{p.status?.replace("_", " ")}</span>
                  </td>
                  <td style={{ ...tdStyle, position: "relative", height: 20, minWidth: 400 }}>
                    {p.plannedStartDate && p.plannedEndDate && (
                      <div style={getBarStyle(p.plannedStartDate, p.plannedEndDate, "#3498db", 0.5)} title={`Planned: ${moment(p.plannedStartDate).format("DD/MM")} – ${moment(p.plannedEndDate).format("DD/MM")}`} />
                    )}
                    {p.performedStartDate && p.performedEndDate && (
                      <div style={{ ...getBarStyle(p.performedStartDate, p.performedEndDate, "#27ae60"), top: 8 }} title={`Performed: ${moment(p.performedStartDate).format("DD/MM")} – ${moment(p.performedEndDate).format("DD/MM")}`} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 8, fontSize: 11, color: "#555" }}>
            <span style={{ display: "inline-block", width: 16, height: 10, background: "#3498db", opacity: 0.5, marginRight: 4 }} /> Planned &nbsp;&nbsp;
            <span style={{ display: "inline-block", width: 16, height: 10, background: "#27ae60", marginRight: 4 }} /> Performed
          </div>
        </div>
      </div>
    );
  }

  renderTableView() {
    const { plans, isLoading } = this.state;
    if (isLoading) return <div style={{ padding: 24, textAlign: "center" }}>Loading...</div>;
    if (!plans.length) return <div style={{ padding: 24, textAlign: "center", color: "#888" }}>No activities found. Click "+ Add Activity" to create one.</div>;

    return (
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#1a5276", color: "#fff" }}>
              <th style={thStyle}>#</th>
              <th style={thStyle}>Activity Name</th>
              <th style={thStyle}>Q</th>
              <th style={thStyle}>Venue</th>
              <th style={thStyle}>Planned Start</th>
              <th style={thStyle}>Planned End</th>
              <th style={thStyle}>Actual Start</th>
              <th style={thStyle}>Actual End</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p._id}>
                <td style={{ ...tdStyle, textAlign: "center" }}>{p.activityNumber}</td>
                <td style={{ ...tdStyle, cursor: "pointer", color: "#2980b9" }} onClick={() => this.openDetailModal(p)}>{p.activityName}</td>
                <td style={{ ...tdStyle, textAlign: "center" }}>Q{p.quarter}</td>
                <td style={tdStyle}>{p.venue || "—"}</td>
                <td style={tdStyle}>{p.plannedStartDate ? moment(p.plannedStartDate).format("DD/MM/YYYY") : "—"}</td>
                <td style={tdStyle}>{p.plannedEndDate ? moment(p.plannedEndDate).format("DD/MM/YYYY") : "—"}</td>
                <td style={{ ...tdStyle, color: p.performedStartDate ? "#1a7c3e" : "#aaa" }}>
                  {p.performedStartDate ? moment(p.performedStartDate).format("DD/MM/YYYY") : "—"}
                </td>
                <td style={{ ...tdStyle, color: p.performedEndDate ? "#1a7c3e" : "#aaa" }}>
                  {p.performedEndDate ? moment(p.performedEndDate).format("DD/MM/YYYY") : "—"}
                </td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  <span style={statusBadge(p.status)}>{p.status?.replace("_", " ")}</span>
                </td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  <button onClick={() => this.openPerformModal(p)} style={actionBtnStyle("#1a7c3e")}>✓ Record</button>
                  {" "}
                  <button onClick={() => this.openEditModal(p)} style={actionBtnStyle("#2980b9")}>Edit</button>
                  {" "}
                  <button onClick={() => this.confirmDelete(p._id)} style={actionBtnStyle("#c0392b")}>Del</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const {
      filterYear, filterQuarter, filterStatus, centers,
      showModal, showDeleteModal, showPerformModal, showDetailModal,
      editRecord, performRecord, detailRecord,
      formYear, formQuarter, formActivityNumber, formActivityName, formDescription,
      formVenue, formParticipants, formResponsibleCenter, formPlannedStartDate,
      formPlannedEndDate, formPlannedBudget, formError, isSaving, isDeleting,
      perfStartDate, perfEndDate, perfVenue, perfParticipants, perfRemarks, perfStatus, isSavingPerf,
      plans, viewMode,
    } = this.state;

    return (
      <div>
        {/* Filters */}
        <div className="no-print row" style={{ marginBottom: 16, alignItems: "flex-end" }}>
          <div className="col-md-2">
            <Select label="Year" options={YEARS} value={YEARS.find((y) => y.value === filterYear) || null}
              onChange={(v) => this.setState({ filterYear: v?.value || currentYear })} />
          </div>
          <div className="col-md-2">
            <Select label="Quarter" options={QUARTERS} value={filterQuarter}
              onChange={(v) => this.setState({ filterQuarter: v })} isClearable />
          </div>
          <div className="col-md-2">
            <Select label="Status" options={STATUS_OPTIONS} value={filterStatus}
              onChange={(v) => this.setState({ filterStatus: v })} isClearable />
          </div>
          <div className="col-md-4" style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
            <Button text="Search" onPress={this.fetchPlans} />
            <Button text="+ Add Activity" onPress={this.openAddModal} className="btn-default" />
            {plans.length > 0 && <Button text="Print" onPress={() => window.print()} className="btn-default" />}
          </div>
        </div>

        {/* View toggle */}
        <div className="no-print" style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <button onClick={() => this.setState({ viewMode: "table" })} style={{ ...toggleBtn, ...(viewMode === "table" ? toggleBtnActive : {}) }}>Table View</button>
          <button onClick={() => this.setState({ viewMode: "gantt" })} style={{ ...toggleBtn, ...(viewMode === "gantt" ? toggleBtnActive : {}) }}>Gantt View</button>
        </div>

        {viewMode === "gantt" ? this.renderGanttView() : this.renderTableView()}

        {/* Add / Edit Modal */}
        <Modal show={showModal} handleClose={this.closeModal} title={editRecord ? "Edit Activity" : "Add Field Activity"} size="lg">
          <div className="modal-body">
            <div className="row">
              <div className="col-md-4">
                <Select label="Year" options={YEARS} value={YEARS.find((y) => y.value === formYear) || null}
                  onChange={(v) => this.setState({ formYear: v?.value || currentYear })} />
              </div>
              <div className="col-md-4">
                <Select label="Quarter" options={QUARTERS} value={formQuarter}
                  onChange={(v) => this.setState({ formQuarter: v })} error={formError.formQuarter} />
              </div>
              <div className="col-md-4">
                <Input label="Activity Number" type="number" value={formActivityNumber}
                  onChange={(e) => this.setState({ formActivityNumber: e.target.value })} error={formError.formActivityNumber} />
              </div>
              <div className="col-md-12">
                <Input label="Activity Name" value={formActivityName}
                  onChange={(e) => this.setState({ formActivityName: e.target.value })} error={formError.formActivityName} />
              </div>
              <div className="col-md-12">
                <Input label="Description" value={formDescription}
                  onChange={(e) => this.setState({ formDescription: e.target.value })} />
              </div>
              <div className="col-md-6">
                <Input label="Venue" value={formVenue}
                  onChange={(e) => this.setState({ formVenue: e.target.value })} />
              </div>
              <div className="col-md-6">
                <Select label="Responsible Center" options={centers} value={formResponsibleCenter}
                  onChange={(v) => this.setState({ formResponsibleCenter: v })} isClearable />
              </div>
              <div className="col-md-12">
                <Input label="Participants (comma-separated)" value={formParticipants}
                  onChange={(e) => this.setState({ formParticipants: e.target.value })} />
              </div>
              <div className="col-md-4">
                <Input label="Planned Start Date" type="date" value={formPlannedStartDate}
                  onChange={(e) => this.setState({ formPlannedStartDate: e.target.value })} error={formError.formPlannedStartDate} />
              </div>
              <div className="col-md-4">
                <Input label="Planned End Date" type="date" value={formPlannedEndDate}
                  onChange={(e) => this.setState({ formPlannedEndDate: e.target.value })} error={formError.formPlannedEndDate} />
              </div>
              <div className="col-md-4">
                <Input label="Planned Budget (RWF)" type="number" value={formPlannedBudget}
                  onChange={(e) => this.setState({ formPlannedBudget: e.target.value })} />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <Button text="Cancel" onPress={this.closeModal} className="btn-default" />
            <Button text={editRecord ? "Update" : "Save"} onPress={this.saveRecord} isSubmitting={isSaving} />
          </div>
        </Modal>

        {/* Performance Modal */}
        <Modal show={showPerformModal} handleClose={this.closePerformModal}
          title={`Record Performance — ${performRecord?.activityName || ""}`} size="md">
          <div className="modal-body">
            {performRecord && (
              <div style={{ background: "#f8f9fa", borderRadius: 6, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
                <strong>Planned:</strong> {performRecord.plannedStartDate ? moment(performRecord.plannedStartDate).format("DD/MM/YYYY") : "—"} –{" "}
                {performRecord.plannedEndDate ? moment(performRecord.plannedEndDate).format("DD/MM/YYYY") : "—"} &nbsp;|&nbsp;
                <strong>Venue:</strong> {performRecord.venue || "—"}
              </div>
            )}
            <div className="row">
              <div className="col-md-6">
                <Input label="Actual Start Date" type="date" value={perfStartDate}
                  onChange={(e) => this.setState({ perfStartDate: e.target.value })} />
              </div>
              <div className="col-md-6">
                <Input label="Actual End Date" type="date" value={perfEndDate}
                  onChange={(e) => this.setState({ perfEndDate: e.target.value })} />
              </div>
              <div className="col-md-6">
                <Input label="Actual Venue" value={perfVenue}
                  onChange={(e) => this.setState({ perfVenue: e.target.value })} />
              </div>
              <div className="col-md-6">
                <Select label="Status" options={STATUS_OPTIONS.filter((s) => s.value !== "planned")}
                  value={perfStatus} onChange={(v) => this.setState({ perfStatus: v })} />
              </div>
              <div className="col-md-12">
                <Input label="Actual Participants (comma-separated)" value={perfParticipants}
                  onChange={(e) => this.setState({ perfParticipants: e.target.value })} />
              </div>
              <div className="col-md-12">
                <Input label="Remarks" value={perfRemarks}
                  onChange={(e) => this.setState({ perfRemarks: e.target.value })} />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <Button text="Cancel" onPress={this.closePerformModal} className="btn-default" />
            <Button text="Save Performance" onPress={this.savePerformance} isSubmitting={isSavingPerf} />
          </div>
        </Modal>

        {/* Detail Modal */}
        <Modal show={showDetailModal} handleClose={this.closeDetailModal}
          title={`Activity ${detailRecord?.activityNumber} — ${detailRecord?.activityName || ""}`} size="md">
          <div className="modal-body">
            {detailRecord && (
              <div style={{ fontSize: 13 }}>
                <div style={detailRow}><strong>Quarter:</strong> Q{detailRecord.quarter}</div>
                <div style={detailRow}><strong>Description:</strong> {detailRecord.description || "—"}</div>
                <div style={detailRow}><strong>Venue:</strong> {detailRecord.venue || "—"}</div>
                <div style={detailRow}><strong>Participants:</strong> {(detailRecord.participants || []).join(", ") || "—"}</div>
                <div style={detailRow}><strong>Responsible Center:</strong> {detailRecord.responsibleCenter?.name || "—"}</div>
                <hr />
                <div style={detailRow}><strong>Planned Start:</strong> {detailRecord.plannedStartDate ? moment(detailRecord.plannedStartDate).format("DD/MM/YYYY") : "—"}</div>
                <div style={detailRow}><strong>Planned End:</strong> {detailRecord.plannedEndDate ? moment(detailRecord.plannedEndDate).format("DD/MM/YYYY") : "—"}</div>
                <div style={detailRow}><strong>Planned Budget:</strong> {detailRecord.plannedBudget ? `${detailRecord.plannedBudget.toLocaleString()} RWF` : "—"}</div>
                <hr />
                <div style={detailRow}><strong>Actual Start:</strong> {detailRecord.performedStartDate ? moment(detailRecord.performedStartDate).format("DD/MM/YYYY") : "—"}</div>
                <div style={detailRow}><strong>Actual End:</strong> {detailRecord.performedEndDate ? moment(detailRecord.performedEndDate).format("DD/MM/YYYY") : "—"}</div>
                <div style={detailRow}><strong>Actual Venue:</strong> {detailRecord.actualVenue || "—"}</div>
                <div style={detailRow}><strong>Actual Participants:</strong> {(detailRecord.actualParticipants || []).join(", ") || "—"}</div>
                <div style={detailRow}><strong>Remarks:</strong> {detailRecord.remarks || "—"}</div>
                <div style={detailRow}><strong>Status:</strong> <span style={statusBadge(detailRecord.status)}>{detailRecord.status?.replace("_", " ")}</span></div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <Button text="Close" onPress={this.closeDetailModal} className="btn-default" />
          </div>
        </Modal>

        <Modal show={showDeleteModal} handleClose={this.closeDeleteModal} title="Delete Activity">
          <DeleteModal handleCloseModal={this.closeDeleteModal} onDelete={this.deleteRecord} isDeleting={isDeleting} />
        </Modal>
      </div>
    );
  }
}

const thStyle = { border: "1px solid #ccc", padding: "6px 8px", textAlign: "left", fontWeight: "bold", fontSize: 12, whiteSpace: "nowrap" };
const tdStyle = { border: "1px solid #ddd", padding: "5px 8px", whiteSpace: "nowrap" };
const actionBtnStyle = (color) => ({ background: color, color: "#fff", border: "none", borderRadius: 3, padding: "3px 8px", cursor: "pointer", fontSize: 11 });
const statusBadge = (status) => ({
  display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: "bold",
  background: STATUS_COLORS[status]?.bg || "#eee",
  color: STATUS_COLORS[status]?.color || "#333",
});
const toggleBtn = { padding: "5px 14px", borderRadius: 4, border: "1px solid #ccc", background: "#fff", cursor: "pointer", fontSize: 12 };
const toggleBtnActive = { background: "#1a5276", color: "#fff", borderColor: "#1a5276" };
const detailRow = { marginBottom: 8, lineHeight: 1.5 };

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return { defaultLanguage };
};

export default connect(mapStateToProps)(FieldActivityPlan);
